import { KeybanUser } from "@keyban/sdk-base";
import {
  AuthSendOtpArgs,
  AuthSignInArgs,
  AuthSignUpArgs,
  AuthUpdateUserArgs,
} from "@keyban/sdk-base/api";
import React from "react";

import { useKeybanClient } from "~/provider";

/**
 * Represents the authentication state of the user.
 *
 * This type can be one of the following states:
 *
 * - **Unintialized**:
 *   - `user`: `undefined`
 *   - `isAuthenticated`: `undefined`
 *
 * - **Unauthenticated**:
 *   - `user`: `null`
 *   - `isAuthenticated`: `false`
 *
 * - **Authenticated**:
 *   - `user`: `KeybanUser`
 *   - `isAuthenticated`: `true`
 */
export type BaseAuth =
  | { user: undefined; isAuthenticated: undefined }
  | { user: null; isAuthenticated: false }
  | { user: KeybanUser; isAuthenticated: true };

/**
 * Represents the authentication context, extending the base authentication state
 * with loading status and methods for various login/logout flows.
 * @property isLoading - Indicates whether an authentication operation is currently in progress.
 * @property sendOtp - Initiates the passwordless login flow by sending an OTP.
 * @property signUp -
 * @property signIn -
 * @property signOut -
 */
export type AuthContext = BaseAuth & {
  /**
   * Indicates whether an authentication operation is currently in progress.
   * True while any login, logout or passwordless flow is pending.
   */
  isLoading: boolean;

  /**
   * Initiates the passwordless login flow by sending an OTP.
   * @param args -
   * @returns A promise that resolves when the OTP is sent.
   * @throws {Error} If sending OTP fails (invalid username or rate limit).
   */
  sendOtp: (args: AuthSendOtpArgs) => Promise<void>;

  /**
   * Initiates login using username and password.
   * @param args -
   * @returns A promise that resolves when authentication succeeds.
   * @throws {Error} If credentials are invalid or login fails.
   */
  signUp: (args: AuthSignUpArgs) => Promise<KeybanUser>;

  /**
   * Initiates login using username and password.
   * @param args -
   * @returns A promise that resolves when authentication succeeds.
   * @throws {Error} If credentials are invalid or login fails.
   */
  signIn: (args: AuthSignInArgs) => Promise<KeybanUser>;

  /**
   * Sign out the current user.
   * @returns A promise that resolves when logout completes.
   * @throws {Error} If sign-out fails.
   */
  signOut: () => Promise<void>;

  updateUser: (args: AuthUpdateUserArgs) => Promise<KeybanUser>;
};

const KeybanAuthContext: React.Context<AuthContext | null> =
  React.createContext<AuthContext | null>(null);

/**
 * KeybanAuthProvider
 * @param props - Component props
 * @param props.children - Children
 * @returns - JSX element
 * @private
 */
export function KeybanAuthProvider({ children }: React.PropsWithChildren) {
  const client = useKeybanClient();

  const [user, setUser] = React.useState<KeybanUser | null>();

  React.useEffect(() => {
    client.api.auth.getUser().then(setUser);
  }, [client]);

  const auth = React.useMemo(
    () => ({
      user,
      isAuthenticated: user === undefined ? undefined : user !== null,
      isLoading: user === undefined,
      sendOtp: client.api.auth.sendOtp,
      signUp: (args: AuthSignUpArgs) =>
        client.api.auth.signUp(args).then((user) => {
          setUser(user);
          return user;
        }),
      signIn: (args: AuthSignInArgs) =>
        client.api.auth.signIn(args).then((user) => {
          setUser(user);
          return user;
        }),
      signOut: () => client.api.auth.signOut().then(() => setUser(null)),
      updateUser: (args: AuthUpdateUserArgs) =>
        client.api.auth.updateUser(args).then((user) => {
          setUser(user);
          return user;
        }),
    }),
    [user, client],
  );

  return (
    <KeybanAuthContext.Provider value={auth as AuthContext}>
      {children}
    </KeybanAuthContext.Provider>
  );
}

/**
 * Custom hook to access the Keyban authentication context.
 *
 * This hook provides access to the authentication state and methods
 * managed by the `KeybanProvider`. It must be called within a component
 * that is a descendant of `KeybanProvider`.
 * @returns The Keyban authentication context object.
 * @throws {Error} If the hook is used outside of a `KeybanProvider`.
 * @example
 * ```tsx
 * import React from 'react';
 * import { useKeybanAuth } from './auth'; // Adjust the import path as needed
 *
 * function MyAuthenticatedComponent() {
 *   const { isAuthenticated, user, login, logout } = useKeybanAuth();
 *
 *   if (!isAuthenticated) {
 *     return <button onClick={login}>Log In</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}!</p>
 *       <button onClick={logout}>Log Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useKeybanAuth() {
  const ctx = React.useContext(KeybanAuthContext);
  if (!ctx)
    throw new Error("useKeybanAuth hook must be used within a KeybanProvider");
  return ctx;
}
